import React from 'react';
import { FiTrendingDown } from 'react-icons/fi';
import { usePointerTracking } from '../hooks/usePointerTracking';

const PLANS = {
  free: {
    name: 'Free',
    priceMonth: 0,
    priceYear: 0,
    subtitle: 'Découvrez PulseCIO, messagerie SaaS gratuite',
    limits: { maxUsers: 1, maxMessages: 30, maxTodos: 15 },
    features: ["1 utilisateur", '30 messages', '15 todos', 'Aucune mention @', 'Support communauté', 'Accès à la messagerie cloud'],
    savings: null,
  },
  starter: {
    name: 'Starter',
    priceMonth: 7.99,
    priceYear: 79.99,
    subtitle: 'Particuliers, petites équipes & associations',
    limits: { maxUsers: 10, maxMessages: 300, maxTodos: 150 },
    features: ["Jusqu'à 10 utilisateurs", '300 messages', '150 todos', 'Mentions @ des messages et todos', 'Dates dynamiques (//Jour+1, //Mois-1…)', 'Paramètres des messages', 'Support email', 'Gestion collaborative cloud'],
    savings: '~262€/mois',
  },
  team: {
    name: 'Team',
    priceMonth: 14.99,
    priceYear: 149.99,
    subtitle: 'PME, équipes structurées & productivité',
    limits: { maxUsers: 30, maxMessages: 500, maxTodos: 500 },
    features: ["Jusqu'à 30 utilisateurs", '500 messages', '500 todos', 'Mentions @ des utilisateurs', 'Notifications in-app', 'Support standard', 'Modèles de messages', 'Onglets personnalisés', 'Exports / Imports CSV/PDF/JSON', 'Productivité cloud'],
    savings: '~795€ à 1185€/mois',
  },
  business: {
    name: 'Business',
    priceMonth: 24.99,
    priceYear: 249.99,
    subtitle: 'Grandes organisations & sécurité avancée',
    limits: { maxUsers: 50, maxMessages: 1000, maxTodos: 1000 },
    features: ["Jusqu'à 50 utilisateurs", '1000 messages', '1000 todos', 'Mentions @ complètes', 'Notifications par email', 'Support prioritaire', 'SSO (sous-domaine personnalisé)', 'Sécurité renforcée SaaS'],
    savings: '~1325€ à 2475€/mois',
  },
  enterprise: {
    name: 'Enterprise',
    priceMonth: 49.99,
    priceYear: 499.99,
    subtitle: 'Groupes, DSI/CIO Office & conformité',
    limits: { maxUsers: 999999, maxMessages: 999999, maxTodos: 999999 },
    features: ['Utilisateurs illimités', 'Messages illimités', 'Todos illimités', 'Branding personnalisé (logo → votre marque)', 'Support dédié 24/7', 'Audit & conformité', 'Onboarding personnalisé', 'Gestion cloud illimitée'],
    savings: '~4950€+/mois (100 users)',
  },
};



const priceLabel = (plan, billingAnnual) => {
  if (plan.priceMonth === 0) return 'Gratuit';
  if (typeof plan.priceMonth === 'string') return plan.priceMonth;
  const price = billingAnnual ? plan.priceYear : plan.priceMonth;
  return billingAnnual ? `€${price}/an` : `€${price}/mois`;
};

const getAnnualAdvantage = (plan) => {
  if (!plan.priceMonth || !plan.priceYear || plan.priceMonth === 0) return null;
  const monthsEquivalent = plan.priceYear / plan.priceMonth;
  if (monthsEquivalent < 12) {
    const percent = Math.round((1 - (plan.priceYear / (plan.priceMonth * 12))) * 100);
    const monthsOff = 12 - monthsEquivalent;
    if (percent >= 10) {
      return `– ${percent} % / – ${monthsOff.toFixed(1)} mois offerts`;
    } else {
      return `– ${monthsOff.toFixed(1)} mois offerts`;
    }
  }
  return null;
};


const Plans = () => {
  usePointerTracking('.plan-card');
  
  const entries = Object.entries(PLANS);
  const [billingAnnual, setBillingAnnual] = React.useState(false);


  // Billing toggle inspired by PricingModal
  const BillingToggle = () => {
    const toggleSwitch = () => setBillingAnnual((v) => !v);
    return (
      <div className="mb-2 flex items-center gap-2 select-none justify-center">
        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Mensuel</div>
        <button
          onClick={toggleSwitch}
          role="switch"
          aria-checked={billingAnnual}
          aria-label={billingAnnual ? 'Facturation annuelle' : 'Facturation mensuelle'}
          className="relative w-12 h-6 rounded-full p-0.5 transition-colors focus:outline-none"
          style={{
            background: billingAnnual ? 'rgba(16,185,129,0.2)' : 'rgba(55,65,81,0.1)'
          }}
        >
          <div
            className="w-5 h-5 rounded-full shadow-sm transition-all duration-300 ease-in-out"
            style={{
              background: billingAnnual ? '#10b981' : '#374151',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transform: billingAnnual ? 'translateX(24px)' : 'translateX(0px)'
            }}
          />
        </button>
        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Annuel</div>
      </div>
    );
  };

  // Calculer l'avantage annuel global (le plus élevé)
  const bestAnnualAdvantage = (() => {
    let best = null;
    entries.forEach(([key, plan]) => {
      const adv = getAnnualAdvantage(plan);
      if (adv) best = adv;
    });
    return best;
  })();

  const plansSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "PulseCIO",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": Object.values(PLANS).map(plan => ({
      "@type": "Offer",
      "name": plan.name,
      "description": plan.subtitle,
      "price": plan.priceMonth === 0 ? "0" : String(plan.priceMonth),
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": "https://pulsecio.com/#plans"
    }))
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(plansSchema) }}
      />
      <svg className="sr-only" style={{overflow: 'visible !important'}} xmlns="http://www.w3.org/2000/svg">
        <filter id="blur-plans" width="400%" height="400%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
        </filter>
      </svg>
      
      <section id="plans" className="py-20">
      <h2 className="text-3xl font-bold mb-4">Tarifs cohérents avec le marché</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-2">Que vous soyez particulier, PME, association ou DSI, vous payez aujourd'hui <strong>Notion (10-18€) + ClickUp (7-12€) + Confluence (5-10€) + outils de com (5-10€)</strong> = <strong>27 à 50€/utilisateur/mois</strong>.</p>
      <p className="text-gray-600 dark:text-gray-300 mb-6">Avec PulseCIO, <strong>tout est unifié dans une plateforme adaptée à toutes les organisations</strong>.</p>

      <div className="flex items-center justify-center gap-3 mb-8">
        <BillingToggle />
        {billingAnnual && bestAnnualAdvantage && (
          <span className="text-xs font-semibold text-green-600 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded shadow-sm">
            {bestAnnualAdvantage}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {entries.map(([key, plan]) => {
          // Highlight recommended plan (Team or Business)
          const isRecommended = key === 'team' || key === 'business';
          return (
            <div
              key={key}
              className={`plan-card relative flex flex-col justify-between p-6 rounded-lg border bg-white dark:bg-gray-800 cursor-pointer overflow-hidden group ${isRecommended ? 'border-blue-400 dark:border-blue-500 ring-1 ring-blue-200 dark:ring-blue-600' : key === 'business' ? 'border-blue-300 dark:border-blue-600' : 'dark:border-gray-700'}`}
              style={{
                '--pointer-x': -10,
                '--pointer-y': -10,
                transform: 'translate3d(0, 0, 0)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                minHeight: '100%' // pour garantir la hauteur
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translate3d(0, 0, 0) scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translate3d(0, 0, 0) scale(1)'}
            >
              {/* Blur background effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-30 pointer-events-none"
                style={{
                  transform: 'translate3d(calc(var(--pointer-x, -10) * 20%), calc(var(--pointer-y, -10) * 20%), 0) scale(1.5)',
                  filter: 'url(#blur-plans) saturate(2) brightness(1.5)',
                  background: isRecommended ? 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, rgba(59,130,246,0.1) 50%, transparent 70%)' : 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, rgba(99,102,241,0.1) 50%, transparent 70%)',
                  willChange: 'transform',
                  transition: 'opacity 0.3s ease'
                }}
              />
              {/* Badge Most popular / Best value */}
              {isRecommended && (
                <div className="absolute top-4 right-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                  {key === 'team' ? 'Plus populaire' : 'Meilleur rapport'}
                </div>
              )}
              <div>
                <div className="flex flex-col items-baseline justify-between mb-3">
                  <h3 className="text-xl font-semibold">{plan.name}</h3>
                  {plan.subtitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{plan.subtitle}</p>
                  )}
                  <div className="text-2xl font-extrabold flex items-center gap-2 mt-2">
                    {priceLabel(plan, billingAnnual)}
                  </div>
                  {plan.savings && (
                    <div className="mt-2 text-xs text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                      <FiTrendingDown className="w-3 h-3 shrink-0" />
                      Économie vs outils séparés : {plan.savings}
                    </div>
                  )}
                </div>

                <ul className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"> 
                      <span className="text-primary dark:text-white mr-2">•</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-2 mt-6">
                <a
                  href="https://app.pulsecio.com/tenant-settings/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md font-semibold shadow hover:bg-blue-700 transition"
                >
                  Démarrer ce plan
                </a>
                <a
                  href="https://doc.pulsecio.com/plans/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full text-center px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-300 rounded-md font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                >
                  Voir la documentation
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
    </>
  );
};

export default Plans;